/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { T, useTranslate } from "@adapptio/lib-components";
import React, { useCallback, useMemo, useState } from "react";
import { termsEditorCommon } from "../../terms/common";
import { ACTION_BUTTON_SIZE, ACTION_BUTTON_STYLE, ActionButton } from "../../ui/components/ActionButton";
import { DialogButtons } from "../../ui/components/Dialogs/DialogButtons";
import { DIALOG_CONTENT_FLOW, DialogContent } from "../../ui/components/Dialogs/DialogContent";
import { DialogMessage } from "../../ui/components/Dialogs/DialogMessage";
import { DIALOG_SIZE, DialogPanel } from "../../ui/components/Dialogs/DialogPanel";
import { FieldGroup } from "../../ui/components/Fields/FieldGroup";
import { FieldText } from "../../ui/components/Fields/FieldText";
import { useStateWithEvent } from "../../ui/hooks";
import { TDialogComponent } from "../Dialog";
import { ConfirmationDialog } from "./ConfirmationDialog";

export const ConfirmationDialogComponent: TDialogComponent<ConfirmationDialog> = ({ dialog }) => {
	const t = useTranslate();
	const params = useMemo(() => dialog.getParams(), [ dialog ]);
	const [ confirmValue, setConfirmValue ] = useState("");

	const isWorking = useStateWithEvent(dialog.onWorkingStateChange, () => dialog.isWorking());

	const handleConfirm = useCallback(() => {
		dialog.confirm();
	}, [ dialog ]);

	const handleSecondConfirm = useCallback(() => {
		dialog.secondConfirm();
	}, [ dialog ]);

	const handleCancel = useCallback(() => {
		dialog.close(false);
	}, [ dialog ]);

	const handleConfirmInputChange = useCallback((value: string) => {
		setConfirmValue(value);
	}, []);

	return (
		<DialogPanel size={DIALOG_SIZE.SMALL}>
			<DialogMessage
				icon={params.icon || "mdi/alert"}
				title={t("editor", params.title, params.translateVars)}
			>
				<p>
					<T domain="editor" term={params.message} vars={params.translateVars} />
				</p>
			</DialogMessage>
			{params.confirmInput ? (
				<DialogContent flow={DIALOG_CONTENT_FLOW.ROW}>
					<FieldGroup>
						<FieldText
							value={confirmValue}
							placeholder={params.confirmInput}
							grow={true}
							onChange={handleConfirmInputChange}
						/>
					</FieldGroup>
				</DialogContent>
			) : null}
			<DialogButtons>
				<ActionButton
					label={t(
						"editor",
						params.cancelLabel || termsEditorCommon.dialog.confirmation.buttonCancel
					)}
					icon={params.cancelIcon || "mdi/close"}
					style={params.cancelStyle || ACTION_BUTTON_STYLE.DEFAULT}
					size={ACTION_BUTTON_SIZE.LARGE}
					disabled={isWorking}
					onClick={handleCancel}
				/>
				{params.secondConfirmLabel && (
					<ActionButton
						label={t(
							"editor",
							params.secondConfirmLabel || termsEditorCommon.dialog.confirmation.buttonConfirm
						)}
						icon={params.secondConfirmIcon || "mdi/check"}
						style={params.secondConfirmStyle || ACTION_BUTTON_STYLE.DEFAULT}
						size={ACTION_BUTTON_SIZE.LARGE}
						disabled={isWorking}
						isLoading={isWorking}
						onClick={handleSecondConfirm}
					/>
				)}
				<ActionButton
					label={t(
						"editor",
						params.confirmLabel || termsEditorCommon.dialog.confirmation.buttonConfirm
					)}
					icon={params.confirmIcon || "mdi/check"}
					style={params.confirmStyle || ACTION_BUTTON_STYLE.PRIMARY}
					size={ACTION_BUTTON_SIZE.LARGE}
					disabled={(params.confirmInput && params.confirmInput !== confirmValue) || isWorking}
					isLoading={isWorking}
					onClick={handleConfirm}
				/>
			</DialogButtons>
		</DialogPanel>
	);
};
